import { createVersionedSchema } from '@mutiny-pkg/versioned-schema/src';
import { ThemePropertiesSchema as ThemePropertiesSchemaV1 } from './version1';
import { ThemePropertiesSchema as ThemePropertiesSchemaV2 } from './version2';
import { ThemePropertiesSchema as ThemePropertiesSchemaV3 } from './version3';
import { ThemePropertiesSchema as ThemePropertiesSchemaV4 } from './version4';

import { defaultThemeV2 } from '@mutiny-pkg/champaign-schemas/src/schemas/themeProperties/oldDefaultTheme/defaultThemeV2';
import { z } from 'zod';

export const ThemePropertiesSchema = createVersionedSchema()
  .defineVersion(ThemePropertiesSchemaV1)
  .defineVersion(ThemePropertiesSchemaV2, (prev) => {
    Object.keys(prev).forEach((key) => {
      if (ThemePropertiesSchemaV2.shape[key as keyof typeof ThemePropertiesSchemaV1.shape] === undefined) {
        delete prev[key as keyof typeof prev];
      }
    });

    return {
      ...defaultThemeV2,
      ...prev,
      version: 2 as const,
    };
  })
  .defineVersion(ThemePropertiesSchemaV3, (prev) => {
    const { fontFace, ...rest } = prev;
    const fontFaces = Object.values(fontFace?.primary ?? {}).map((value) => ({ ...value, fontStyle: 'normal' }));

    const composedTheme = {
      ...rest,
      fontFaces,
      version: 3 as const,
    };

    Object.values(composedTheme.typography).forEach((font) => {
      if (font.fontFamily) {
        return;
      }

      const typographyFont = fontFaces.find(
        (ff) => ff.fontFamily && ff.src && (ff.fontWeight === font.fontWeight || ff.fontWeight === '100 900'),
      );
      font.fontFamily = typographyFont?.fontFamily ?? '';
    });

    return composedTheme;
  })
  .defineVersion(ThemePropertiesSchemaV4, (prev) => {
    const { color } = prev;
    const { brandDark, brandLight, brandMedium, ...rest } = color;
    return {
      ...prev,
      color: { ...rest, primary: brandDark, secondary: brandMedium, tertiary: brandLight },
      additionalColors: [],
      logos: {
        light: '',
        dark: '',
      },
      version: 4 as const,
    };
  });

type ThemeProperties = z.infer<typeof ThemePropertiesSchema.latestVersionSchema>;
type ColorThemeInput = Pick<ThemeProperties, 'color' | 'additionalColors'>;

export const getMainThemeColors = (theme: ColorThemeInput): string[] => {
  const { primary, secondary, tertiary } = theme?.color ?? {};
  return Object.values({ primary, secondary, tertiary })
    .concat(theme?.additionalColors)
    .filter((identity) => Boolean(identity)) as string[];
};

const getCTAColors = (theme: ThemeProperties, ctaType: 'primary' | 'secondary' | 'tertiary') => {
  const { backgroundColor, hoverBackgroundColor, color, hoverColor } = theme?.cta?.[ctaType] ?? {};
  return Object.values({ backgroundColor, hoverBackgroundColor, color, hoverColor }).filter((identity) =>
    Boolean(identity),
  ) as string[];
};

export function getThemeColors(theme: ThemeProperties) {
  const mainThemeColors = getMainThemeColors(theme);
  const colors = [
    ...mainThemeColors,
    ...getCTAColors(theme, 'primary'),
    ...getCTAColors(theme, 'secondary'),
    ...getCTAColors(theme, 'tertiary'),
  ];
  const uniqueColors = Array.from(new Set(colors));

  return uniqueColors;
}
